import {useMemo} from 'react'
import dayjs from '@/dayjs'
import {AccessesColumn, AccessesSearchParams, ValidatorSearchParams} from '../types'
import {TableFiltersValidationSchema} from '@/types'
import {z} from 'zod'
import {extractFilterValue} from '@/utilities/utils'

const AllAccessesSearchParams = ValidatorSearchParams.merge(AccessesSearchParams.pick({filters: true}))
type AllAccessesSearchParams = z.infer<typeof AllAccessesSearchParams>

type UseAccessesSearchParamsReturn = {
    tableSearchParams: string
    validatorSearchParams: string
}

type FilterField = TableFiltersValidationSchema['fields'][number]

// Helper function to create table search params
const createTableSearchParams = (searchParams: AllAccessesSearchParams): string => {
    const filters: FilterField[] = []

    // Add consultant filter
    if (searchParams.consultant) {
        filters.push({
            key: AccessesColumn.enum.consultantId,
            operator: 'in',
            value: [searchParams.consultant]
        } as FilterField)
    }

    // Add patient filter
    if (searchParams.patient) {
        filters.push({
            key: AccessesColumn.enum.patientId,
            operator: 'in',
            value: [searchParams.patient]
        } as FilterField)
    }

    if (filters.length === 0) {
        return ''
    }

    const tableFilters: TableFiltersValidationSchema = {
        operator: 'and',
        fields: filters
    }

    if (searchParams.startDate && searchParams.endDate) {
        return new URLSearchParams({
            filters: JSON.stringify(tableFilters),
            startDate: searchParams.startDate,
            endDate: searchParams.endDate,
            range: 'month'
        }).toString()
    } else {
        return new URLSearchParams({
            filters: JSON.stringify(tableFilters)
        }).toString()
    }
}

// Helper function to create validator search params
const createValidatorSearchParams = (searchParams: AllAccessesSearchParams): string => {
    if (!searchParams.filters) {
        return ''
    }

    const parsedFilters: TableFiltersValidationSchema = TableFiltersValidationSchema.parse(
        JSON.parse(searchParams.filters)
    )

    if (parsedFilters.operator === 'or') {
        return ''
    }

    // Extract consultant filter
    const consultantFilter = extractFilterValue(parsedFilters, AccessesColumn.enum.consultantId) as string[] | undefined

    // Early return if no consultant or multiple consultants
    if (!consultantFilter || consultantFilter.length !== 1) {
        return ''
    }

    const consultant = consultantFilter[0]
    const params: Record<string, string> = {consultant}

    // Extract patient filter
    const patientFilter = extractFilterValue(parsedFilters, AccessesColumn.enum.patientId) as string[] | undefined

    if (patientFilter && patientFilter.length === 1) {
        params.patient = patientFilter[0]
    }

    // Extract date filters
    const startDate = searchParams.startDate
    const endDate = searchParams.endDate

    if (startDate && endDate && dayjs(startDate).isSame(dayjs(endDate), 'month')) {
        if (
            dayjs(startDate).isSame(dayjs(startDate).startOf('month'), 'day') &&
            dayjs(endDate).isSame(dayjs(endDate).endOf('month'), 'day') &&
            dayjs(endDate).isAfter(dayjs().endOf('month').subtract(1, 'year'))
        ) {
            params.startDate = startDate
            params.endDate = endDate
        }
    }

    return new URLSearchParams(params).toString()
}

export const useAccessesPersistantSearchParams = (
    searchParams: AllAccessesSearchParams
): UseAccessesSearchParamsReturn => {
    const tableSearchParams = useMemo(
        () => createTableSearchParams(searchParams),
        [searchParams.consultant, searchParams.startDate, searchParams.endDate, searchParams.patient]
    )

    const validatorSearchParams = useMemo(
        () => createValidatorSearchParams(searchParams),
        [searchParams.filters, searchParams.startDate, searchParams.endDate]
    )

    return {
        tableSearchParams,
        validatorSearchParams
    }
}
